import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useAuthStore } from '../stores/authStore'
import { LoginForm } from '../components/LoginForm'
import { ThemeToggle } from '../components/ThemeToggle'

interface LoginSearch {
  redirect?: string
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: LoginPage,
})

function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const navigate = useNavigate()
  const { redirect } = useSearch({ from: '/login' })

  // Redirect to home if already authenticated
  if (isAuthenticated && !isLoading) {
    navigate({ to: redirect ?? '/' })
    return null
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center px-4 animate-gradient-bg"
      data-testid="login-page"
    >
      {/* Theme toggle - top right */}
      <div className="absolute top-4 right-4 z-10" data-testid="login-theme-toggle">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        {/* IRIS Branding */}
        <div className="mb-8 text-center" data-testid="login-branding">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--color-accent)]">
            <svg
              className="h-8 w-8 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="4" />
              <line x1="21.17" y1="8" x2="12" y2="8" />
              <line x1="3.95" y1="6.06" x2="8.54" y2="14" />
              <line x1="10.88" y1="21.94" x2="15.46" y2="14" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            IRIS
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Intelligence Platform
          </p>
        </div>

        {/* Login Card */}
        <div
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-[var(--shadow-lg)]"
          data-testid="login-card"
        >
          <h2 className="mb-6 text-lg font-semibold text-[var(--color-card-foreground)]">
            Sign in to your account
          </h2>
          <LoginForm redirectTo={redirect} />

          {/* SSO Divider */}
          <div className="relative my-6" data-testid="sso-divider">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--color-border)]" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[var(--color-card)] px-3 text-[var(--color-text-muted)]">
                or sign in with
              </span>
            </div>
          </div>

          {/* SSO Placeholder Buttons */}
          <div className="flex flex-col gap-2" data-testid="sso-buttons">
            <button
              type="button"
              disabled
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] opacity-50 cursor-not-allowed"
              title="Coming Soon"
              data-testid="sso-google"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </button>
            <button
              type="button"
              disabled
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] opacity-50 cursor-not-allowed"
              title="Coming Soon"
              data-testid="sso-microsoft"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
                <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
                <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
              </svg>
              Microsoft
            </button>
            <button
              type="button"
              disabled
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] opacity-50 cursor-not-allowed"
              title="Coming Soon"
              data-testid="sso-saml"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              SAML SSO
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
